import { Box, ButtonBase, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import type { FC } from ".."
import { ResponsiveImage } from ".."
import StyledContainer from "./StyledContainer"
import filmFestivalGlobals from "./assets/filmFestivalGlobals.json"

const SubmissionTypeSection: FC<{
  handleOpenDrawer: (variant: "pro" | "globetrotter") => void
}> = ({ handleOpenDrawer }) => {
  const globals = filmFestivalGlobals.submissionTypeSection
  return (
    <Box
      id="submission-type-section"
      sx={(theme) => ({
        backgroundColor: theme.palette["neutral.700"].main,
      })}
    >
      <StyledContainer maxWidth={1400} variant="secondary">
        <Box
          minHeight="100vh"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          py={{ xs: 14, lg: 10 }}
        >
          {/* <StyledTypography variant="body.sm" margin="0 auto">
            {globals.quote1}
          </StyledTypography> */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", lg: "repeat(3, 1fr)" }}
            gap={{ xs: 14, lg: 10 }}
            sx={(theme) => ({
              transition: "all 0.3s ease-in-out",
              [theme.breakpoints.down("lg")]: {
                padding: theme.spacing(12, 2),
              },
            })}
          >
            <Card
              title={globals.cards.globetrotter.title}
              subtitle={globals.cards.globetrotter.subtitle}
              description={globals.cards.globetrotter.description}
              ctaText={globals.cards.globetrotter.ctaText}
              variant="globetrotter"
              onClick={() => handleOpenDrawer("globetrotter")}
            />
            <TextInfo
              title={globals.title}
              keyword={globals.keyword}
              subtitle1={globals.subtitle1}
              subtitle2={globals.subtitle2}
              subtitle3={globals.subtitle3}
            />
            <Card
              title={globals.cards.pro.title}
              subtitle={globals.cards.pro.subtitle}
              description={globals.cards.pro.description}
              ctaText={globals.cards.pro.ctaText}
              variant="pro"
              onClick={() => handleOpenDrawer("pro")}
            />
          </Box>
          {/* <Box display="flex" justifyContent="space-between" gap={2}>
            <StyledTypography variant="body.sm">{globals.quote2}</StyledTypography>
            <StyledTypography variant="body.sm">{globals.quote3}</StyledTypography>
          </Box> */}
        </Box>
      </StyledContainer>
    </Box>
  )
}

export default SubmissionTypeSection

// A sub component for the quote
const StyledTypography = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette["primary.800"].main};
    font-size: 9px!important;
    font-weight: 500;
    line-height: 90%!important;
    text-transform: uppercase;
  `
)

interface CardProps {
  title: string
  subtitle: string
  description: string
  ctaText: string
  variant: "globetrotter" | "pro"
  onClick?: () => void
}

// A sub component for the card
const Card: FC<CardProps> = ({ title, subtitle, description, ctaText, variant, onClick }) => {
  const imageUrl =
    variant === "globetrotter" ? "/images/globetrotter-card.jpg" : "/images/pro-card.jpg"
  const image = {
    variants: [
      {
        width: 127,
        height: 127,
        url: imageUrl,
        title: "",
        alt: "a couple traveling by longtail boat exploring epic limestone cliffs in a huge lake in Khao Sok National Park, Chiew Lan Lake, Thailand Surat Thani, men and woman in front of longtail boat",
        style: "default",
      },
    ],
  }
  return (
    <Stack
      sx={(theme) => ({
        backgroundColor: theme.palette["tertiary.800"].main,
        borderRadius: theme.spacing(6),
        padding: theme.spacing(16, 11, 14),
        maxWidth: 382,
        alignItems: "center",
        order: { xs: 2, lg: 1 },
        ...(variant === "pro" && {
          backgroundColor: theme.palette["primary.800"].main,
          marginLeft: "auto",
          order: 3,
        }),
        [theme.breakpoints.down("lg")]: {
          margin: "0 auto",
          padding: theme.spacing(8, 12),
        },
      })}
    >
      <Box position="relative">
        <ResponsiveImage
          image={image}
          sx={(theme) => ({
            width: "127px",
            height: "127px",
            objectFit: "cover",
            borderRadius: theme.spacing(4),
          })}
        />
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#FF8F52",
            mixBlendMode: "overlay",
            opacity: 0.4,
          })}
        />
      </Box>
      <Stack
        mt={{ xs: 6, lg: 8 }}
        sx={(theme) => ({
          "> span": {
            color:
              variant === "pro"
                ? theme.palette["tertiary.800"].main
                : theme.palette["primary.800"].main,
            fontSize: "40px",
            lineHeight: "100%!important",
            textAlign: "center",
          },
        })}
      >
        <Typography variant="heading.xl" sx={(theme) => ({ fontWeight: 700 })}>
          {title}
        </Typography>
        <Typography variant="heading.xl" sx={(theme) => ({ fontWeight: 400, fontStyle: "italic" })}>
          {subtitle}
        </Typography>
      </Stack>
      <Typography
        variant="body.md"
        mt={{ xs: 4, lg: 8 }}
        sx={(theme) => ({
          fontSize: "16px!important",
          color:
            variant === "pro"
              ? theme.palette["tertiary.800"].main
              : theme.palette["neutral.700"].main,
          fontWeight: 600,
          lineHeight: "140%!important",
          textAlign: "center",
        })}
      >
        {description}
      </Typography>
      <ButtonBase
        onClick={onClick}
        sx={(theme) => ({
          ...theme.typography["link.md"],
          "display": "flex",
          "alignItems": "center",
          "gap": 2,
          "minHeight": 49,
          "padding": theme.spacing(2, 4),
          "width": "100%",
          "mt": { xs: 4, lg: 10 },
          "backgroundColor":
            variant === "pro"
              ? theme.palette["neutral.700"].main
              : theme.palette["primary.800"].main,
          "borderRadius": 999,
          "transition": theme.transitions.create(["background-color", "border-color", "color"]),
          "&:hover": {
            backgroundColor:
              variant === "pro"
                ? theme.palette["neutral.800"].main
                : theme.palette["primary.900"].main,
          },
        })}
      >
        <Typography
          variant="body.md"
          fontWeight={600}
          sx={(theme) => ({
            color: theme.palette["neutral.100"].main,
            lineHeight: "160%!important",
            textTransform: "uppercase",
            fontSize: "13px",
          })}
        >
          {ctaText}
        </Typography>
      </ButtonBase>
    </Stack>
  )
}

interface TextInfoProps {
  title: string
  keyword: string
  subtitle1: string
  subtitle2: string
  subtitle3: string
}

// A sub component for the text info
const TextInfo: FC<TextInfoProps> = ({ title, keyword, subtitle1, subtitle2, subtitle3 }) => {
  return (
    <Stack justifyContent="center" order={{ xs: 1, lg: 2 }}>
      <Stack
        sx={(theme) => ({
          "textAlign": "center",
          "> span": {
            color: theme.palette["tertiary.800"].main,
            fontSize: { xs: "64px", lg: "72px" },
            lineHeight: "100%!important",
          },
        })}
      >
        <Typography variant="heading.md" sx={(theme) => ({})}>
          {title}
        </Typography>
        <Typography
          variant="heading.md"
          sx={(theme) => ({ fontStyle: "italic", fontWeight: 400, pt: 2 })}
        >
          {keyword}
        </Typography>
      </Stack>
      <Stack
        sx={(theme) => ({
          "textAlign": "center",
          "margin": "0 auto",
          "mt": { xs: 8, lg: 12 },
          "maxWidth": { xs: "100%", md: "310px" },
          "> span": {
            color: theme.palette["tertiary.800"].main,
            fontSize: "24px",
            lineHeight: "110%!important",
            fontWeight: 600,
          },
        })}
      >
        <Typography variant="body.md" sx={(theme) => ({})}>
          {subtitle1}
        </Typography>
        <Typography
          variant="body.md"
          sx={(theme) => ({ color: `${theme.palette["primary.800"].main}!important` })}
        >
          {subtitle2}
        </Typography>
        <Typography variant="body.md" sx={(theme) => ({})}>
          {subtitle3}
        </Typography>
      </Stack>
    </Stack>
  )
}
